import React from 'react';
import { useTranslation } from '../../../i18n';
import type { OneBrainDirection } from './OneBrainMaze';

interface OneBrainControlsProps {
  controls: OneBrainDirection[];
  disabled?: boolean;
  onMove: (direction: OneBrainDirection) => void;
}

const DIR_LABEL: Record<OneBrainDirection, string> = {
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
};

/**
 * Only shows the directions this player controls — core One Brain mechanic.
 */
export const OneBrainControls: React.FC<OneBrainControlsProps> = ({
  controls,
  disabled,
  onMove,
}) => {
  const { t } = useTranslation();

  if (controls.length === 0) {
    return (
      <p style={{ textAlign: 'center', opacity: 0.7, margin: 0 }}>
        {t('gameplay.oneBrain.noControls')}
      </p>
    );
  }

  const has = (d: OneBrainDirection) => controls.includes(d);

  const btnStyle = (active: boolean): React.CSSProperties => ({
    width: 72,
    height: 72,
    borderRadius: 16,
    border: active ? '2px solid #F97316' : '2px solid transparent',
    background: active
      ? 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)'
      : 'rgba(255,255,255,0.06)',
    color: 'white',
    fontSize: '1.75rem',
    fontWeight: 800,
    cursor: active && !disabled ? 'pointer' : 'default',
    opacity: !active ? 0.25 : disabled ? 0.45 : 1,
    pointerEvents: active && !disabled ? 'auto' : 'none',
    touchAction: 'manipulation',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <p style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', opacity: 0.8, textAlign: 'center' }}>
        {t('gameplay.oneBrain.yourControls')}
      </p>
      <button type="button" style={btnStyle(has('up'))} onClick={() => onMove('up')} aria-label={t('gameplay.oneBrain.dirUp')}>
        {DIR_LABEL.up}
      </button>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" style={btnStyle(has('left'))} onClick={() => onMove('left')} aria-label={t('gameplay.oneBrain.dirLeft')}>
          {DIR_LABEL.left}
        </button>
        <button type="button" style={btnStyle(has('down'))} onClick={() => onMove('down')} aria-label={t('gameplay.oneBrain.dirDown')}>
          {DIR_LABEL.down}
        </button>
        <button type="button" style={btnStyle(has('right'))} onClick={() => onMove('right')} aria-label={t('gameplay.oneBrain.dirRight')}>
          {DIR_LABEL.right}
        </button>
      </div>
    </div>
  );
};
